import { Alert } from "components/alert/alert";
import getTranslation from "helpers/translation/getTranslation";
import formatPrice from "helpers/format-price/formatPrice";
import { toggleCssClass } from "helpers/toggle/toggle";
import axios from "axios";
import { deleteCookie, getCookie } from "../../helpers/cookies/cookies";

// Global Config
const compareWidgetConfig = (function () {
  const config = {
    maxProducts: 5,
    sessionStorage: {
      name: "swproductcompare",
    },
    data: {
      compareUrl: null,
      products: {},
    },
    ui: {
      domElements: {
        productCheckboxes: document.querySelectorAll(
          'input[type="checkbox"][name="compare"]',
        ),
        compareContainer: document.querySelector("#compare-container"),
        compareTotalsBadge: document.querySelector("#compare-products-count"),
      },
      maxAmountAlert: null,
    },
  };

  return {
    config: config,
  };
})();

// DATA CONTROLLER
const compareWidgetData = (function (Config) {
  // fetches data from server and saves new product to internal storage and to cookie
  const addProduct = (id, callback) => {
    _fetchFromServer(id, (data) => {
      // push into all products
      Config.config.data.products[data.data.productid] = data.data;
      saveToSessionStorage();

      // return added item
      callback(Config.config.data.products[data.data.productid]);
      return id;
    });
  };

  // get product from internal storage by id
  const getProduct = (id) => {
    if (!Config.config.data.products[id]) {
      return null;
    }
    return Config.config.data.products[id];
  };

  // removes product from internal storage and saves to cookie
  const remove = (id) => {
    // remove item from data.products
    if (Config.config.data.products[id]) {
      delete Config.config.data.products[id];
      saveToSessionStorage();
    }

    // return id of item to remove
    return id;
  };

  // get total nr of products in internal storage
  const getTotals = () => {
    return Object.keys(Config.config.data.products).length;
  };

  // get all products from internal storage
  const getAllProducts = () => {
    return Config.config.data.products;
  };

  const saveToSessionStorage = () => {
    window.sessionStorage.setItem(
      Config.config.sessionStorage.name,
      JSON.stringify(Config.config.data),
    );
  };

  const getFromSessionStorage = () => {
    const sessionData = window.sessionStorage.getItem(
      Config.config.sessionStorage.name,
    );
    if (sessionData) {
      Config.config.data.products = JSON.parse(sessionData).products;
    }

    return;
  };

  const _fetchFromServer = (id, callback) => {
    const url = `${Config.config.data.compareUrl}product/${id}`;
    axios.get(url).then((data) => {
      callback(data);
    });
  };

  // public functions
  return {
    addProduct: addProduct,
    getProduct: getProduct,
    remove: remove,
    getTotals: getTotals,
    getAllProducts: getAllProducts,
    saveToSessionStorage: saveToSessionStorage,
    getFromSessionStorage: getFromSessionStorage,
  };
})(compareWidgetConfig);

// UI CONTROLLER
const compareWidgetUI = (function (config, data) {
  const addListItem = (id) => {
    const product = data.getProduct(id);
    config.config.ui.domElements.compareContainer
      .querySelector(".compare-widget-content")
      .insertAdjacentHTML("beforeend", _renderTemplate(product));

    // Calculate in another thread to prevent miscalculation
    setTimeout(() => {
      handleContainerDisplay();
    }, 1);

    _handleAnimation();
  };

  const removeListItem = (id) => {
    config.config.ui.domElements.compareContainer
      .querySelector(`#product-${id}`)
      .remove();

    const checkboxes = document.querySelectorAll(
      `input[type="checkbox"][data-productid="${id}"]`,
    );
    if (checkboxes) {
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });
    }

    // update ui (amount of products etc)
    // Calculate in another thread to prevent miscalculation
    setTimeout(() => {
      handleContainerDisplay();
    }, 1);

    if (data.getTotals() < 5) {
      _handleDisableMaxAmountReached();
    }
  };

  const handleContainerDisplay = () => {
    if (data.getTotals() > 0) {
      config.config.ui.domElements.compareContainer.classList.add("is-shown");

      if (data.getTotals() > 1) {
        config.config.ui.domElements.compareContainer
          .querySelector('[data-action="compare-show"]')
          .classList.remove("d-none");
      } else {
        config.config.ui.domElements.compareContainer
          .querySelector('[data-action="compare-show"]')
          .classList.add("d-none");
      }
    } else {
      config.config.ui.domElements.compareContainer.classList.remove(
        "is-shown",
      );
      config.config.ui.domElements.compareContainer.classList.remove(
        "is-active",
      );
    }

    // update count badge
    // Calculate in another thread to prevent miscalculation
    setTimeout(() => {
      config.config.ui.domElements.compareTotalsBadge.textContent = String(
        Object.keys(config.config.data.products).length,
      );
    }, 1);
  };

  // render product row template with correct data
  const _renderTemplate = (data) => {
    return `<div class="d-flex flex-row py-2 border-bottom" id="product-${data.productid}"
             data-product-code="${data.productcode}">
            <div class="col-auto">
                <a href="${data.url}">
                    <img src="${data.mainimage}"
                         width="60">
                </a>
            </div>
            <div class="col">
                <a href="${data.url}"
                   title="${data.naam}">
                    <h5 class="text-0 mb-0 product-name">${data.naam}</h5>
                </a>
                <div class="price-box">
                    <div class="end-unit-price">
                        <span class="unit-end-price">${formatPrice(data.prijs)}</span>
                    </div>
                </div>
            </div>
            <div class="col-auto">
                <a href="#"
                data-id="${data.productid}"
                   data-action="compare-remove"
                   class="btn btn-grey btn-sm">
                    ${config.config.ui.domElements.compareContainer.dataset.closeIcon}
                </a>
            </div>
        </div>`;
  };

  const handleCheckboxChange = (e) => {
    // get productId
    const productId = e.target.dataset.productid;

    // get all checkboxes with same productId (mobile + desktop)
    const checkboxes = document.querySelectorAll(
      `input[type="checkbox"][data-productid="${productId}"]`,
    );

    // check if item needs to be added or removed
    if (e.target.checked === true) {
      // check all checkboxes with same productId
      checkboxes.forEach((checkbox) => {
        checkbox.checked = true;
      });

      if (data.getTotals() >= config.config.maxProducts) {
        _handleEnableMaxAmountReached(e);
        return;
      }

      // add item to data controller
      data.addProduct(productId, () => {
        // add item to ui
        addListItem(productId);
      });
    } else {
      // uncheck all checkboxes with same productId
      checkboxes.forEach((checkbox) => {
        checkbox.checked = false;
      });

      // remove item from ui
      removeListItem(productId);
      data.remove(productId);

      if (data.getTotals() < config.config.maxProducts) {
        _handleDisableMaxAmountReached(e);
      }
    }
  };

  const moveToCompare = () => {
    const url = config.config.data.compareUrl;
    const ids = [];

    Object.keys(data.getAllProducts()).forEach((product) => {
      ids.push(product);
    });

    window.location = `${url}${ids.join()}`;
  };

  const _handleAnimation = () => {
    toggleCssClass(
      config.config.ui.domElements.compareContainer.querySelector(
        ".compare-widget-header",
      ),
      "animation-product-added",
    );

    window.setTimeout(() => {
      toggleCssClass(
        config.config.ui.domElements.compareContainer.querySelector(
          ".compare-widget-header",
        ),
        "animation-product-added",
      );
    }, 1000);
  };

  const _handleEnableMaxAmountReached = (e) => {
    e.target.checked = false;
    // show alert saying max amount of products is reached
    config.config.ui.maxAmountAlert = new Alert({
      container: ".compare-widget-content",
      id: "compareMaxAmount",
    });
    config.config.ui.maxAmountAlert.setVariant("error");
    config.config.ui.maxAmountAlert.setMessage(
      getTranslation("compare", "COMPARE_PRODUCT_AMOUNT_REACHED"),
    );
    config.config.ui.maxAmountAlert.show();
    config.config.ui.domElements.compareContainer.classList.add("is-active");

    const checkboxes = document.querySelectorAll(
      'input[type="checkbox"][name="compare"]:not(:checked)',
    );
    checkboxes.forEach((checkbox) => {
      checkbox.disabled = true;
    });
  };

  const _handleDisableMaxAmountReached = () => {
    if (config.config.ui.maxAmountAlert) {
      config.config.ui.maxAmountAlert.hide();
    }

    const checkboxes = document.querySelectorAll(
      'input[type="checkbox"][name="compare"]:not(:checked)',
    );
    checkboxes.forEach((checkbox) => {
      checkbox.disabled = false;
    });
  };

  return {
    addListItem: addListItem,
    removeListItem: removeListItem,
    handleContainerDisplay: handleContainerDisplay,
    moveToCompare: moveToCompare,
    handleCheckboxChange: handleCheckboxChange,
  };
})(compareWidgetConfig, compareWidgetData);

// GLOBAL APP CONTROLLER
export const appController = (function (data, UI, config) {
  const init = () => {
    if (config.config.ui.domElements.compareContainer) {
      config.config.data.compareUrl = document.querySelector(
        ".product-compare-widget",
      ).dataset.compareUrl;

      _restoreDataFromCookie();
      _setupEventListeners();
    }
    _eatDaCookie();
  };

  // Before switching to session storage we used a cookie for this. We want to delete the cookie if it still exists
  // on someone's device
  // Can be removed after 06-04-2024
  const _eatDaCookie = () => {
    const cookie = getCookie("swproductcompare");
    if (cookie) {
      deleteCookie("swproductcompare");
    }
  };

  const _restoreDataFromCookie = () => {
    // Restore data from the cookie (on page load)
    data.getFromSessionStorage();
    const products = config.config.data.products;

    Object.keys(products).forEach((key) => {
      UI.addListItem(products[key].productid);

      const checkboxes = document.querySelectorAll(
        `input[type="checkbox"][data-productid="${products[key].productid}"]`,
      );
      if (checkboxes) {
        checkboxes.forEach((checkbox) => {
          checkbox.checked = true;
        });
      }
    });

    UI.handleContainerDisplay();
  };

  // setup event listeners
  const _setupEventListeners = () => {
    document.querySelector("body").addEventListener("change", (e) => {
      const compareCheckbox = e.target.closest(
        'input[type="checkbox"][name="compare"]',
      );

      if (compareCheckbox) {
        UI.handleCheckboxChange(e);
      }
    });

    document.querySelector("body").addEventListener("click", (e) => {
      const deleteButton = e.target.closest('[data-action="compare-remove"]');
      const compareToggle = e.target.closest('[data-action="compare-toggle"]');
      const compareShow = e.target.closest('[data-action="compare-show"]');

      if (deleteButton) {
        e.preventDefault();
        const id = deleteButton.dataset.id;
        const refreshPage = deleteButton.hasAttribute("data-refresh");
        data.remove(id);
        UI.removeListItem(id);

        if (refreshPage) {
          UI.moveToCompare();
        }
      }

      if (compareToggle) {
        e.preventDefault();
        toggleCssClass(
          config.config.ui.domElements.compareContainer,
          "is-active",
        );
      }

      if (compareShow) {
        e.preventDefault();
        UI.moveToCompare();
      }
    });
  };

  return {
    init: init,
  };
})(compareWidgetData, compareWidgetUI, compareWidgetConfig);
